import { inject, Injectable } from '@angular/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, tap, throttleTime } from 'rxjs';
import { StorageService } from '@app/shared';
import { appActions } from '@app/store/app.actions';
import { deviceActions } from '@app/store/device/actions/device.actions';
import { updateActions } from '../actions/update.actions';
import { UPDATE_RELOCK_KEY } from '../keys';
import { selectBundleId } from '../selectors/update.selector';

const CHECK_THROTTLE = 600_000;

@Injectable()
export class UpdateDeviceBridge {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly storageService = inject(StorageService);

    // The app can run for weeks without restarting, so launch alone is not a check tick.
    // Unlock is: the app is foregrounded, the network is up and an operator is present.
    // Throttled: dozens of lock/unlock cycles per shift must not each hit the manifest.
    checkOnUnlock$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(deviceActions.unlock),
            throttleTime(CHECK_THROTTLE),
            map(() => updateActions.check()),
        );
    });

    // Lock is the apply point: no work is in flight and the operator has walked away, so the
    // reload costs nothing. Landing it here rather than at unlock means the new bundle is
    // already up before they come back.
    applyOnLock$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(deviceActions.lock),
                concatLatestFrom(() => this.store.select(selectBundleId)),
                filter(([, bundleId]) => !!bundleId),
                tap(([, bundleId]) => {
                    // Must be written before set(), and synchronously: set() destroys the JS
                    // context, so an async write could lose the race and boot unlocked.
                    this.storageService.setItem(UPDATE_RELOCK_KEY, true);
                    void CapacitorUpdater.set({ id: bundleId! });
                }),
            );
        },
        { dispatch: false },
    );

    // Reset (logout / user switch) is the other apply point: a session boundary with no work in
    // flight. No relock flag — a logged-out boot is gated by the PIN login page itself, and a
    // user-switch boot has a freshly authenticated session. Relies on clearState exempting the
    // update slice, or the bundle id would be wiped before this effect reads it.
    applyOnReset$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(appActions.reset),
                concatLatestFrom(() => this.store.select(selectBundleId)),
                filter(([, bundleId]) => !!bundleId),
                tap(([, bundleId]) => void CapacitorUpdater.set({ id: bundleId! })),
            );
        },
        { dispatch: false },
    );

    // One-shot: only a boot that follows an update reload re-locks. A plain cold start finds no
    // flag and is left alone.
    restoreLock$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(updateActions.restoreLock),
            filter(() => this.storageService.getItem<boolean>(UPDATE_RELOCK_KEY) === true),
            tap(() => this.storageService.removeItem(UPDATE_RELOCK_KEY)),
            map(() => deviceActions.lock()),
        );
    });
}
